import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { css } from '@emotion/react';
import { getRedirectResult } from 'firebase/auth';
import { FullScreenLoading } from '~/components/common';
import { OAuthProviders } from '~/types';
import { SNSButtons, SNSButton, PAGE_ROUTE } from '~/constants';
import AuthService from '~/services/AuthService';
import { auth } from '~/services/firebase';
import { useAuthStore, useSignUpStepStore } from '~/store';
import useToast from '~/store/useToast';
import SNSLoginButton from './SNSSignInButton/SNSSignInButton';

const isDev = import.meta.env.DEV;

export default function SNSSignIn() {
  const [isLoading, setIsLoading] = useState(false);
  const { showToast } = useToast();
  const navigate = useNavigate();
  const { setStep, setType } = useSignUpStepStore((state) => ({
    setStep: state.setStep,
    setType: state.setType,
  }));

  const handleOnClick = async (title: OAuthProviders) => {
    setIsLoading(true);
    try {
      if (isDev) {
        // 개발 환경인 경우 팝업으로 로그인
        const result = await AuthService.signInWithOAuthPopup(title);
        if (!result) return showToast('error', 'Failed to sign in');
        const isVerified = await AuthService.verifySignIn();
        if (isVerified) {
          navigate(PAGE_ROUTE.MAIN);
        } else {
          setStep('Agreement');
          setType('sns');
          navigate(PAGE_ROUTE.SIGNUP);
        }
      } else {
        // 배포 환경인 경우 리다이렉트로 로그인
        await AuthService.signInWithRedirect(title);
      }
    } catch (error) {
      showToast('error', 'Failed to sign in');
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const handleRedirectResult = async () => {
      const { idToken, authProvider, isAuthenticated } =
        useAuthStore.getState();
      // 이미 최상단에서 firebase 로그인 처리가 안되었으면 조치없이 리턴
      if (isAuthenticated) return navigate(PAGE_ROUTE.MAIN);
      if (!idToken || authProvider !== 'firebase') return;
      const { user } = (await getRedirectResult(auth)) ?? {};
      // redirect 결과가 없으면 조치없이 리턴
      if (!user) return;
      // 이미 isAuthenticated가 아닌 상태에서 세션이 있음 = 유저 정보가 없음
      setStep('Agreement');
      setType('sns');
      navigate(PAGE_ROUTE.SIGNUP);
    };
    handleRedirectResult();
  }, []);

  useEffect(() => {
    const reload = (e: PageTransitionEvent) => {
      if (e.persisted) {
        window.location.reload();
      }
    };
    window.addEventListener('pageshow', reload);

    return () => {
      window.removeEventListener('pageshow', reload);
    };
  }, []);

  return (
    <>
      {isLoading && <FullScreenLoading />}
      <ul
        css={css`
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 8px;
          width: 100%;
        `}
      >
        {SNSButtons.map(({ title, icon }: SNSButton) => (
          <SNSLoginButton
            onClick={() => handleOnClick(title)}
            key={`SNSLogin_${title}`}
            title={title}
            icon={icon}
          />
        ))}
      </ul>
    </>
  );
}
